import { motion, useScroll, useTransform } from "framer-motion";
import { WaitlistForm } from "./WaitlistForm";

const EASE = [0.16, 1, 0.3, 1];
const HERO_IMG =
  "https://images.unsplash.com/photo-1518708909080-704599b19972?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHx3b21hbiUyMHdlbGxuZXNzJTIwbWVkaXRhdGlvbiUyMHNlcmVuZSUyMGNhbG0lMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODk1ODA5Njd8MA&ixlib=rb-4.1.0&q=85";

const RevealLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, 70]);
  const yLogo = useTransform(scrollY, [0, 700], [0, -50]);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-16"
    >
      <div
        className="animate-soft-pulse pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 12%, rgba(245, 230, 227, 0.75) 0%, rgba(250, 246, 240, 0) 62%)",
        }}
      />

      <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-4 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:px-12">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
          >
            <span
              data-testid="hero-tagline-pill"
              className="inline-block rounded-full border border-[rgba(195,153,83,0.35)] bg-white/70 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-gold-dark backdrop-blur-xl"
            >
              strength • balance • self
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="mt-8 font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl"
          >
            <RevealLine delay={0.4}>Your personal wellness</RevealLine>
            <RevealLine delay={0.52}>coach, for</RevealLine>
            <RevealLine delay={0.64}>
              <em className="text-gold-dark">every part of you.</em>
            </RevealLine>
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="mt-7 max-w-md text-base leading-relaxed text-[#5C5450] sm:text-lg"
          >
            Personalised wellness for your strength, balance and self — designed around you.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
            className="mt-9"
          >
            <WaitlistForm />
            <p className="mt-4 text-xs tracking-wide text-[#8A8079]" data-testid="hero-microcopy">
              Begin with 1 week free · then ₹499/month
            </p>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            style={{ y: yImg }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.3, delay: 0.5, ease: EASE }}
            className="relative mx-auto w-full max-w-sm lg:max-w-none"
          >
            <div className="absolute -inset-6 rounded-[3rem] bg-blush/60 blur-2xl" />
            <div className="relative aspect-[4/5] overflow-hidden rounded-[2.5rem] shadow-[0_30px_60px_-20px_rgba(26,24,23,0.25)]">
              <motion.img
                src={HERO_IMG}
                alt="A woman in a calm, serene wellness moment"
                data-testid="hero-image"
                initial={{ scale: 1.15 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.8, delay: 0.5, ease: EASE }}
                className="h-full w-full object-cover"
              />
            </div>

            <motion.div
              style={{ y: yLogo }}
              initial={{ opacity: 0, y: 24, rotate: -6 }}
              animate={{ opacity: 1, y: 0, rotate: -4 }}
              transition={{ duration: 1.2, delay: 1.1, ease: EASE }}
              whileHover={{ rotate: 0, scale: 1.03 }}
              className="absolute -bottom-8 -left-4 w-36 overflow-hidden rounded-3xl border border-white/70 shadow-[0_20px_45px_-15px_rgba(26,24,23,0.35)] sm:-left-10 sm:w-44"
            >
              <img
                src="/logo.png"
                alt="841 wellness — strength, balance, self"
                data-testid="hero-logo"
                className="h-full w-full object-cover"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
